import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, goldPerEach, qty, esoIconUrl, ESO_ICON_FALLBACK, qualityColor } from '../lib/format.js';
import { pickItem } from '../lib/picks.js';
import RelTime from '../components/RelTime.jsx';
import TradeWeekPicker from '../components/TradeWeekPicker.jsx';
import wm from '../lib/wmStore.js';
import { EMOJI } from '../lib/emoji.js';
import TraderPersona from './TraderPersona.jsx';

/**
 * Personal sales summary. Stat strip + biggest sale + recent rows.
 * Operates on the viewer's @-tag (server resolves it).
 *
 * #290 — Defaults to "all time / most-recent N" on first open so the
 * widget always shows useful data, and uses the shared TradeWeekPicker
 * for arrows + center button + calendar / all-time, matching Leaderboard
 * and KioskTracking. Selections persist back to widget settings via the
 * window-manager store.
 */
export default function MySales(props) {
  const [salesTab, setSalesTab] = createSignal(
    props.widget?.type === 'trader-persona' || props.settings?.tab === 'persona' ? 'persona' : 'sales'
  );
  // #290 — initial range derivation:
  //   * explicit dateFrom/dateTo or weekFrom/weekTo win (advanced settings)
  //   * else explicit weekOffset (including 0 = current week)
  //   * else default to {weekOffset: -1} = all-time / recent N
  function deriveInitialRange() {
    const s = props.settings || {};
    if (s.dateFrom && s.dateTo) {
      return { startTs: s.dateFrom, endTs: s.dateTo + 86400 };
    }
    if (Number.isFinite(+s.weekFrom) && Number.isFinite(+s.weekTo)) {
      // Picker only knows weekOffset/startTs/endTs; if a legacy week-range
      // is configured we surface its newer end as the picker's starting
      // value. The advanced settings still flow through to the backend.
      return { weekOffset: Math.max(0, Math.min(+s.weekFrom, +s.weekTo)) };
    }
    if (s.weekOffset === -1 || s.weekOffset === '-1') return { weekOffset: -1 };
    if (Number.isFinite(+s.weekOffset)) return { weekOffset: Math.max(0, +s.weekOffset) };
    return { weekOffset: -1 };
  }

  const [range, setRange] = createSignal(deriveInitialRange());

  function persistRange(next) {
    setRange(next);
    if (!props.widget?.id) return;
    try {
      const prev = props.widget.settings || {};
      // When the user picks a date range or all-time, clear advanced
      // legacy keys (weekFrom/weekTo/dateFrom/dateTo) so they don't
      // override the picker on next open.
      const cleared = {
        ...prev,
        weekOffset: null, weekFrom: null, weekTo: null,
        dateFrom: null,   dateTo:   null,
      };
      let patch;
      if (next.startTs != null && next.endTs != null) {
        // dateTo in settings is the inclusive *start-of-day*; the backend
        // adds 24h itself. Store the picker's exclusive endTs minus 24h
        // back into dateTo so the round-trip is stable.
        patch = { ...cleared, dateFrom: next.startTs, dateTo: Math.max(next.startTs, next.endTs - 86400) };
      } else {
        patch = { ...cleared, weekOffset: next.weekOffset };
      }
      wm.setSettings(props.widget.id, patch);
    } catch { /* persistence is best-effort */ }
  }

  // Live mode (#110) — visual indicator only; SSE refetch is owned by
  // useWidgetData via the 'sales' topic.
  const live = () => !!props.settings?.live;

  const { data, loading, error } = useWidgetData('my-sales', () => {
    const r = range();
    const s = props.settings || {};
    if (r.startTs != null && r.endTs != null) {
      return { dateFrom: r.startTs, dateTo: Math.max(r.startTs, r.endTs - 86400) };
    }
    if (r.weekOffset === -1) return { weekOffset: -1 };
    // Honour legacy weekFrom/weekTo advanced settings only when the
    // picker is still on the same starting offset they imply.
    if (Number.isFinite(+s.weekFrom) && Number.isFinite(+s.weekTo)) {
      return { weekFrom: +s.weekFrom, weekTo: +s.weekTo };
    }
    return { weekOffset: r.weekOffset || 0 };
  });
  const seller = createMemo(() => (data()?.sellers || [])[0] || null);
  const sales = createMemo(() => (seller()?.sales || []).slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
  const weeklySummary = createMemo(() => data()?.weeklySummary || null);
  const note = () => data()?.note;

  const rangeLabel = () => {
    const r = range();
    if (r.startTs != null && r.endTs != null) {
      const fmt = (sec) => { try { return new Date(sec * 1000).toISOString().slice(0, 10); } catch { return '?'; } };
      return `${fmt(r.startTs)} → ${fmt(r.endTs - 86400)}`;
    }
    if (r.weekOffset === -1) return 'all time · recent';
    const w = +(r.weekOffset || 0);
    return w === 0 ? 'this week' : (w === 1 ? 'last week' : `${w} weeks ago`);
  };

  return (
    <div class="v2-widget">
      <div class="v2-tabs" role="tablist" aria-label="My Sales & Persona" style={{ 'margin-bottom': '6px' }}>
        <button
          type="button"
          role="tab"
          aria-selected={salesTab() === 'sales'}
          class={`v2-tab ${salesTab() === 'sales' ? 'is-active' : ''}`}
          onClick={() => setSalesTab('sales')}
        >
          💰 Sales Ledger
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={salesTab() === 'persona'}
          class={`v2-tab ${salesTab() === 'persona' ? 'is-active' : ''}`}
          onClick={() => setSalesTab('persona')}
        >
          🎭 Trader Persona & Notes
        </button>
      </div>
      <Show when={salesTab() === 'persona'}>
        <TraderPersona {...props} />
      </Show>
      <Show when={salesTab() === 'sales'}>
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__sub">{rangeLabel()}</span>
        <Show when={live()}>
          <span class="v2-live-pill" title="Live mode — refreshes when new sales arrive">🔴 LIVE</span>
        </Show>
        <span style={{ 'margin-left': 'auto' }}>
          <TradeWeekPicker
            value={range()}
            onChange={(next) => persistRange(next)}
            maxWeeksAgo={51}
            allowAllTime
            allowCustom
          />
        </span>
      </div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <Show when={!note()} fallback={<div class="v2-empty">{note()}</div>}>
            <Show when={seller()} fallback={<div class="v2-empty">No sales for this period.</div>}>
              <div class="v2-widget__scroll">
                <div class="v2-stat-grid">
                  <div class="v2-stat v2-stat--gold">
                    <span class="v2-stat__label">Gold</span>
                    <span class="v2-stat__value">{gold(seller().profit || 0)}</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">Sales</span>
                    <span class="v2-stat__value">{qty(seller().saleCount || 0)}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">Items</span>
                    <span class="v2-stat__value">{qty(seller().totalItems || 0)}</span>
                  </div>
                </div>
                <Show when={weeklySummary()}>
                  <div class="v2-section-h">{EMOJI.weekly} Weekly Sales</div>
                  <div class="v2-stat-grid">
                    <div class="v2-stat v2-stat--gold">
                      <span class="v2-stat__label">Weekly gold</span>
                      <span class="v2-stat__value">{gold(weeklySummary().totalGold || 0)}</span>
                    </div>
                    <div class="v2-stat v2-stat--cyan">
                      <span class="v2-stat__label">Weekly sales</span>
                      <span class="v2-stat__value">{qty(weeklySummary().totalSales || 0)}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Weekly items</span>
                      <span class="v2-stat__value">{qty(weeklySummary().totalItems || 0)}</span>
                    </div>
                  </div>
                </Show>
                <Show when={seller().biggestSale}>
                  <div class="v2-section-h">💰 Biggest sale</div>
                  <div class="v2-sale-row">
                    <RelTime class="v2-sale-row__time" ts={seller().biggestSale.timestamp} />
                    <span class="v2-sale-row__main">
                      <Show when={seller().biggestSale.itemIcon}>
                        <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(seller().biggestSale.itemIcon)} alt=""
                          onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                      </Show>
                      <b class="v2-pick v2-pick--item" data-icon={seller().biggestSale.itemIcon || ''}
                        style={seller().biggestSale.quality ? { color: qualityColor(seller().biggestSale.quality) } : undefined}
                        onClick={() => pickItem(seller().biggestSale.itemName, { icon: seller().biggestSale.itemIcon })}>{seller().biggestSale.itemName || '—'}</b>
                      {seller().biggestSale.quant > 1 ? <em> ×{seller().biggestSale.quant}</em> : null}
                    </span>
                    <span class="v2-sale-row__price">
                      {gold(seller().biggestSale.price)}
                      {seller().biggestSale.quant > 1 ? <em class="v2-sale-row__per"> {goldPerEach(seller().biggestSale.price, seller().biggestSale.quant)}</em> : null}
                    </span>
                  </div>
                </Show>
                <Show when={sales().length}>
                  <div class="v2-section-h">Recent</div>
                  <div>
                    <For each={sales().slice(0, 50)}>{(s) => (
                      <div class="v2-sale-row">
                        <RelTime class="v2-sale-row__time" ts={s.timestamp} />
                        <span class="v2-sale-row__main">
                          <Show when={s.itemIcon}>
                            <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(s.itemIcon)} alt=""
                              onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                          </Show>
                          <b class="v2-pick v2-pick--item" data-icon={s.itemIcon || ''}
                            style={s.quality ? { color: qualityColor(s.quality) } : undefined}
                            onClick={() => pickItem(s.itemName, { icon: s.itemIcon })}>{s.itemName || '—'}</b>
                          {s.quant > 1 ? <em> ×{s.quant}</em> : null}
                        </span>
                        <span class="v2-sale-row__price">
                          {gold(s.price)}
                          {s.quant > 1 ? <em class="v2-sale-row__per"> {goldPerEach(s.price, s.quant)}</em> : null}
                        </span>
                      </div>
                    )}</For>
                  </div>
                </Show>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
      </Show>
    </div>
  );
}